import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { FiPlus, FiTrash, FiEdit2, FiLoader } from 'react-icons/fi';
import { IoDocumentAttach } from "react-icons/io5";
import axiosInstance from '../../config/axiosConfig';

const DocumentLinksDialog = ({ patientId }) => {
  const [documents, setDocuments] = useState([]);
  const [newLink, setNewLink] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingLink, setEditingLink] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const isDisabled = !patientId;

  // Fetch documents when dialog opens
  useEffect(() => {
    if (dialogOpen && patientId) {
      fetchDocuments();
    }
  }, [dialogOpen, patientId]);

  const fetchDocuments = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get(`/PatientDocument/get-documents/${patientId}`);
      if (response.data && response.data.result) {
        setDocuments(response.data.result);
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError('Failed to load documents. Please try again.');
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddDocument = async () => {
    if (!newLink.trim() || !newDescription.trim()) {
      setError('Please fill in both fields.');
      return;
    }

    // Basic URL validation
    try {
      new URL(newLink.trim());
    } catch {
      setError('Please enter a valid URL.');
      return;
    }

    setIsAdding(true);
    setError('');

    try {
      const documentModel = {
        PatientId: parseInt(patientId),
        DocumentLink: newLink.trim(),
        LinkText: newDescription.trim()
      };

      const response = await axiosInstance.post('/PatientDocument/add-document', documentModel);
      
      if (response.data && response.data.result) {
        // Refresh the documents list
        await fetchDocuments();
        setNewLink('');
        setNewDescription('');
      } else {
        setError('Failed to add document. Please try again.');
      }
    } catch (error) {
      console.error('Error adding document:', error);
      setError(error.response?.data?.errorResponse?.message || 'Failed to add document. Please try again.');
    } finally {
      setIsAdding(false);
    }
  };

  const handleUpdateDocument = async (documentId) => {
    if (!editingLink.trim() || !editingDescription.trim()) {
      setError('Please fill in both fields.');
      return;
    }

    // Basic URL validation
    try {
      new URL(editingLink.trim());
    } catch {
      setError('Please enter a valid URL.');
      return;
    }

    setError('');

    try {
      const documentModel = {
        Id: documentId,
        PatientId: parseInt(patientId),
        DocumentLink: editingLink.trim(),
        LinkText: editingDescription.trim()
      };

      const response = await axiosInstance.put('/PatientDocument/update-document', documentModel);
      
      if (response.data && response.data.result) {
        // Refresh the documents list
        await fetchDocuments();
        setEditingId(null);
        setEditingLink('');
        setEditingDescription('');
      } else {
        setError('Failed to update document. Please try again.');
      }
    } catch (error) {
      console.error('Error updating document:', error);
      setError(error.response?.data?.errorResponse?.message || 'Failed to update document. Please try again.');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    setError('');

    try {
      const response = await axiosInstance.delete(`/PatientDocument/delete-document/${documentId}`);
      
      if (response.data && response.data.result) {
        // Refresh the documents list
        await fetchDocuments();
      } else {
        setError('Failed to delete document. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      setError(error.response?.data?.errorResponse?.message || 'Failed to delete document. Please try again.');
    }
  };

  const startEditing = (doc) => {
    setEditingId(doc.id);
    setEditingLink(doc.documentLink);
    setEditingDescription(doc.linkText);
    setError(''); // Clear any existing errors
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingLink('');
    setEditingDescription('');
    setError('');
  };

  const clearError = () => {
    setError('');
  };

  return (
    <Dialog.Root open={dialogOpen} onOpenChange={setDialogOpen}>
      <Dialog.Trigger asChild disabled={isDisabled}>
        <button className={isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}>
           <IoDocumentAttach
             color={isDisabled ? 'gray' : 'green'}
             fontSize={24}
           />
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md bg-white rounded-lg p-6 shadow-xl max-h-[80vh] overflow-y-auto">
          <Dialog.Title className="text-xl font-bold mb-4">Document Links</Dialog.Title>
          
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 rounded">
              <div className="flex justify-between items-start">
                <p className="text-red-700 text-sm">{error}</p>
                <button 
                  onClick={clearError}
                  className="text-red-500 hover:text-red-700 ml-2"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <FiLoader className="animate-spin mr-2" size={20} />
              <span className="text-gray-600">Loading documents...</span>
            </div>
          ) : (
            <>
              {/* Documents List */}
              <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                {documents.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No documents found.</p>
                ) : (
                  documents.map((doc) => (
                    <div key={doc.id} className="bg-gray-100 p-3 rounded">
                      {editingId === doc.id ? (
                        // Edit Mode
                        <div className="space-y-2">
                          <input 
                            type="text" 
                            value={editingDescription}
                            onChange={(e) => setEditingDescription(e.target.value)}
                            placeholder="Enter document description" 
                            className="w-full border rounded p-2 text-sm"
                          />
                          <input 
                            type="url" 
                            value={editingLink}
                            onChange={(e) => setEditingLink(e.target.value)}
                            placeholder="Enter document link" 
                            className="w-full border rounded p-2 text-sm"
                          />
                          <div className="flex gap-2">
                            <button 
                              onClick={() => handleUpdateDocument(doc.id)}
                              className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                            >
                              Save
                            </button>
                            <button 
                              onClick={cancelEditing}
                              className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <a 
                              href={doc.documentLink} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-blue-600 hover:text-blue-800 block truncate"
                              title={doc.linkText}
                            >
                              {doc.linkText}
                            </a>
                            <p className="text-xs text-gray-500 truncate" title={doc.documentLink}>
                              {doc.documentLink}
                            </p>
                          </div>
                          <div className="flex gap-1 ml-2">
                            <button 
                              onClick={() => startEditing(doc)}
                              className="text-blue-500 hover:bg-blue-100 p-1 rounded"
                              title="Edit document"
                            >
                              <FiEdit2 size={14} />
                            </button>
                            <button 
                              onClick={() => handleDeleteDocument(doc.id)} 
                              className="text-red-500 hover:bg-red-100 p-1 rounded"
                              title="Delete document"
                            >
                              <FiTrash size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Add New Document Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">Add New Document</h3>
                <div className="space-y-2 mb-3">
                  <input 
                    type="text" 
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="Enter document description" 
                    className="w-full border rounded p-2"
                    disabled={isAdding}
                  />
                  <input 
                    type="url" 
                    value={newLink}
                    onChange={(e) => setNewLink(e.target.value)}
                    placeholder="Enter document link (https://...)" 
                    className="w-full border rounded p-2"
                    disabled={isAdding}
                  />
                </div>
                
                <button 
                  onClick={handleAddDocument} 
                  disabled={isAdding || !newLink.trim() || !newDescription.trim()}
                  className="bg-green-500 text-white p-2 rounded flex items-center justify-center w-full disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isAdding ? (
                    <>
                      <FiLoader className="animate-spin mr-2" size={16} />
                      Adding...
                    </>
                  ) : (
                    <>
                      <FiPlus size={16} className="mr-1" /> Add Document
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          <Dialog.Close asChild>
            <button 
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
              aria-label="Close"
            >
              ✕
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DocumentLinksDialog;